'use client'

import { Alert, Stack, Typography } from '@mui/material'
import { type FileUIPart, isFileUIPart } from 'ai'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react'

import { type ChatTurnRequest, type Quota, useLazyGetConversationQuery } from '~/data/api/client/assistant'
import { type AssistantUIMessage, toUIMessages } from '~/data/api/client/assistant/enhancedEndpoints'
import { logger } from '~/debug/logger'
import type { FocusedContextRef } from '~/features/assistant/contextRef'

import ChatErrorState from './ChatErrorState'
import Composer from './Composer'
import EmptyState from './EmptyState'
import MessageItem from './MessageItem'
import { useActMode } from './actMode'
import AttachmentDropZone from './attachments/AttachmentDropZone'
import useAttachmentUploads from './attachments/useAttachmentUploads'
import { type ComposedMessage, readContextRefs } from './contextRefs'
import ThinkingShimmer from './parts/ThinkingShimmer'
import { classifyChatError } from './stream/transport'
import mergeStoredMessages from './stream/mergeStoredMessages'
import useAssistantChat from './useAssistantChat'
import useStickToBottom from './useStickToBottom'

type ChatSessionProps = {
  conversationId: string
  organizationId: string
  initialMessages: AssistantUIMessage[]
  initialQuota?: Quota
  /** A conversation shared with the viewer, who may read it but not send to it. */
  readOnly?: boolean
  /**
   * Sent as the first message once the session mounts, for a conversation created from the empty state. Its tagged
   * entities stay in the composer, as they do after any send.
   */
  initialPrompt?: ComposedMessage
  /** Files attached to that first message, already uploaded to the conversation. */
  initialFiles?: FileUIPart[]
}

/** Below this many remaining messages the thread shows the organization's allowance. */
const QUOTA_WARNING_THRESHOLD = 5

/** One open conversation: its transcript, the turn in flight, and the composer. */
export default function ChatSession({
  conversationId,
  organizationId,
  initialMessages,
  initialQuota,
  readOnly = false,
  initialPrompt,
  initialFiles
}: ChatSessionProps) {
  const t = useTranslations('features.AssistantV2')
  const { messages, setMessages, sendMessage, status, error, stop, quota } = useAssistantChat({
    conversationId,
    organizationId,
    initialMessages,
    initialQuota
  })
  const uploads = useAttachmentUploads(useCallback(async () => conversationId, [conversationId]))
  const { actMode } = useActMode()
  const [contextRefs, setContextRefs] = useState<FocusedContextRef[]>(initialPrompt?.contextRefs ?? [])
  const sentInitialPrompt = useRef(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const { stickToBottom } = useStickToBottom(scrollRef, contentRef)
  const [fetchConversation] = useLazyGetConversationQuery()
  const [hasUnreadOutcome, setHasUnreadOutcome] = useState(false)
  const markUnreadOutcome = useCallback(() => setHasUnreadOutcome(true), [])

  // The tagged entities ride on the message, where the transcript keeps them, and on the request, where
  // assistant-api reads them. `autoApprove` is the Ask / Act choice at the moment of sending.
  const sendWith = (text: string, refs: FocusedContextRef[], files: FileUIPart[] = []) => {
    stickToBottom()
    return sendMessage(
      {
        text,
        ...(files.length > 0 && { files }),
        ...(refs.length > 0 && { metadata: { contextRefs: refs } })
      },
      {
        body: { contextRefs: refs, autoApprove: actMode } satisfies Pick<ChatTurnRequest, 'contextRefs' | 'autoApprove'>
      }
    )
  }

  const sendInitialPrompt = useEffectEvent(
    (prompt: ComposedMessage, files: FileUIPart[]) => void sendWith(prompt.text, prompt.contextRefs, files)
  )

  useEffect(() => {
    if (initialPrompt === undefined || sentInitialPrompt.current) return
    sentInitialPrompt.current = true
    sendInitialPrompt(initialPrompt, initialFiles ?? [])
  }, [initialPrompt, initialFiles])

  const isBusy = status === 'submitted' || status === 'streaming'

  const isBusyRef = useRef(isBusy)
  useEffect(() => {
    isBusyRef.current = isBusy
  }, [isBusy])

  // assistant-api posts a message when a long-running action finishes. `useChat` writes a reply in flight into
  // the last message, so the transcript is read back and merged only while no reply streams; a turn sent while
  // the read is in flight defers the merge until that turn ends.
  useEffect(() => {
    if (!hasUnreadOutcome || isBusy) return
    setHasUnreadOutcome(false)
    fetchConversation({ conversationId })
      .unwrap()
      .then((conversation) => {
        if (isBusyRef.current) {
          setHasUnreadOutcome(true)
          return
        }
        const stored = toUIMessages(conversation.messages)
        setMessages((current) => mergeStoredMessages(current, stored))
      })
      .catch((loadError: unknown) => logger.error('Failed to load an assistant action outcome', loadError))
  }, [hasUnreadOutcome, isBusy, fetchConversation, conversationId, setMessages])

  const isQuotaSpent = quota?.remaining === 0
  // Quota, credit and access failures repeat on every send until something outside the chat changes.
  const isBlocked = isQuotaSpent || (!!error && ['quota', 'credit', 'access'].includes(classifyChatError(error)))
  const lastMessage = messages.at(-1)
  const send = (text: string) => void sendWith(text, contextRefs)
  const sendDraft = (text: string) => {
    void sendWith(text, contextRefs, uploads.files)
    uploads.clear()
  }

  // assistant-api accepts only `submit-message`, so a failed turn is retried by sending its question again
  // rather than with `regenerate`.
  const retry = () => {
    const lastUserIndex = messages.findLastIndex((message) => message.role === 'user')
    const question = messages[lastUserIndex]
    if (!question) return
    const text = question.parts.flatMap((part) => (part.type === 'text' ? [part.text] : [])).join('\n')
    setMessages(messages.slice(0, lastUserIndex))
    void sendWith(text, readContextRefs(question), question.parts.filter(isFileUIPart))
  }

  return (
    <AttachmentDropZone onFiles={uploads.add} disabled={readOnly || isBlocked}>
      <Stack ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        <Stack ref={contentRef} spacing={2}>
          {/* A conversation opened with a first message never shows the empty state, even for the paint before it is sent. */}
          {messages.length === 0 && !isBusy && !initialPrompt && (
            <EmptyState onSuggestion={readOnly || isBlocked || uploads.isUploading ? undefined : sendDraft} />
          )}
          {messages.map((message) => (
            <MessageItem
              key={message.id}
              message={message}
              conversationId={conversationId}
              readOnly={readOnly}
              isStreaming={isBusy && message === lastMessage}
              canAnswer={!readOnly && !isBlocked && !isBusy && message === lastMessage}
              onAnswer={(answer) => send(answer)}
              onActionSettled={markUnreadOutcome}
            />
          ))}
          {isBusy && lastMessage?.role !== 'assistant' && <ThinkingShimmer />}
        </Stack>
      </Stack>
      <Stack spacing={1} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        {readOnly ? (
          <Alert severity="info">{t('readOnly')}</Alert>
        ) : (
          <>
            {error && <ChatErrorState error={error} onRetry={retry} />}
            {!error && isQuotaSpent && (
              <Alert severity="warning" data-testid="assistant-quota-exceeded">
                {t('quotaExceeded')}
              </Alert>
            )}
            {quota && !isQuotaSpent && quota.remaining <= QUOTA_WARNING_THRESHOLD && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="assistant-quota-remaining">
                {t('quotaRemaining', { remaining: quota.remaining })}
              </Typography>
            )}
            <Composer
              onSend={sendDraft}
              onStop={isBusy ? () => void stop() : undefined}
              disabled={isQuotaSpent || isBlocked}
              contextRefs={contextRefs}
              onContextRefsChange={setContextRefs}
              attachments={uploads.attachments}
              onAttach={uploads.add}
              onRemoveAttachment={uploads.remove}
            />
          </>
        )}
      </Stack>
    </AttachmentDropZone>
  )
}
